import React, { useState, useEffect } from 'react';
import axios from 'axios';

import { API_URL } from '../config';

const getColor = (v) => {
  const n = parseInt(v) || 0;
  if (n <= 20) return '#FF3B30';
  if (n <= 40) return '#FF9500';
  if (n <= 60) return '#FFCC00';
  if (n <= 80) return '#34C759';
  return '#007AFF';
};

const getLabel = (v) => {
  const n = parseInt(v) || 0;
  if (n <= 20) return 'Extreme Fear';
  if (n <= 40) return 'Fear';
  if (n <= 60) return 'Neutral';
  if (n <= 80) return 'Greed';
  return 'Extreme Greed';
};

const FearGreedPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/fear-greed?limit=30`);
        setData(res.data);
      } catch (e) {
        console.error('Error fetching fear & greed:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const current = data[0];
  const numVal = current ? parseInt(current.value) || 0 : 0;
  const rotation = (numVal / 100) * 180 - 90;
  const color = getColor(numVal);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(parseInt(timestamp) * 1000);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const maxBarVal = Math.max(...data.map(d => parseInt(d.value) || 0), 1);

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-black text-3xl uppercase text-white mb-2" data-testid="fear-greed-title">
            Fear & Greed Index
          </h1>
          <p className="text-[#8892B0]">Live crypto market sentiment from Alternative.me</p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#8892B0]">Loading index...</div>
        ) : !current ? (
          <div className="text-center py-16 bg-[#131313] border border-white/5">
            <p className="text-[#8892B0]">Unable to load Fear & Greed data</p>
          </div>
        ) : (
          <>
            {/* Main Gauge */}
            <div className="bg-[#131313] border border-white/5 p-8 mb-6" data-testid="fear-greed-main">
              <div className="flex flex-col items-center">
                {/* Large Gauge */}
                <div className="relative w-64 h-32 overflow-hidden mb-4" data-testid="fear-greed-gauge">
                  <svg viewBox="0 0 200 100" className="w-full h-full">
                    <defs>
                      <linearGradient id="gaugeGradLg" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#FF3B30" />
                        <stop offset="25%" stopColor="#FF9500" />
                        <stop offset="50%" stopColor="#FFCC00" />
                        <stop offset="75%" stopColor="#34C759" />
                        <stop offset="100%" stopColor="#007AFF" />
                      </linearGradient>
                    </defs>
                    <path d="M 10 95 A 90 90 0 0 1 190 95" fill="none" stroke="#1A1A1A" strokeWidth="16" strokeLinecap="round" />
                    <path d="M 10 95 A 90 90 0 0 1 190 95" fill="none" stroke="url(#gaugeGradLg)" strokeWidth="14" strokeLinecap="round" />
                    {/* Scale labels */}
                    <text x="15" y="85" fill="#5C6B82" fontSize="8" textAnchor="middle">0</text>
                    <text x="55" y="25" fill="#5C6B82" fontSize="8" textAnchor="middle">25</text>
                    <text x="100" y="12" fill="#5C6B82" fontSize="8" textAnchor="middle">50</text>
                    <text x="145" y="25" fill="#5C6B82" fontSize="8" textAnchor="middle">75</text>
                    <text x="185" y="85" fill="#5C6B82" fontSize="8" textAnchor="middle">100</text>
                  </svg>
                  {/* Needle */}
                  <div className="absolute bottom-0 left-1/2 origin-bottom" style={{ transform: `translateX(-50%) rotate(${rotation}deg)`, transition: 'transform 1s ease-out' }}>
                    <div className="w-0.5 h-24 bg-white rounded-full shadow-lg shadow-white/20" />
                  </div>
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-white shadow-lg shadow-white/30" />
                </div>

                <span className="text-6xl font-black mt-2" style={{ color }} data-testid="fear-greed-value">
                  {numVal}
                </span>
                <span className="text-xl font-bold uppercase tracking-widest mt-2" style={{ color }} data-testid="fear-greed-label">
                  {current.value_classification}
                </span>
                <p className="text-xs text-[#5C6B82] mt-3">Updated daily &middot; 0 = Extreme Fear &middot; 100 = Extreme Greed</p>
              </div>
            </div>

            {/* Sentiment Scale */}
            <div className="bg-[#131313] border border-white/5 p-6 mb-6">
              <h3 className="text-xs text-[#8892B0] uppercase tracking-widest font-bold mb-4">Sentiment Scale</h3>
              <div className="flex gap-1 h-8 rounded overflow-hidden">
                {[
                  { label: 'Extreme Fear', range: '0-20', color: '#FF3B30', width: '20%' },
                  { label: 'Fear', range: '21-40', color: '#FF9500', width: '20%' },
                  { label: 'Neutral', range: '41-60', color: '#FFCC00', width: '20%' },
                  { label: 'Greed', range: '61-80', color: '#34C759', width: '20%' },
                  { label: 'Extreme Greed', range: '81-100', color: '#007AFF', width: '20%' },
                ].map((seg) => (
                  <div
                    key={seg.label}
                    className="relative flex items-center justify-center group"
                    style={{ backgroundColor: seg.color, width: seg.width }}
                  >
                    <span className="text-[10px] font-bold text-black/70 hidden sm:inline">{seg.label}</span>
                    {numVal >= parseInt(seg.range.split('-')[0]) && numVal <= parseInt(seg.range.split('-')[1]) && (
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-transparent border-t-white" />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[10px] text-[#5C6B82]">0</span>
                <span className="text-[10px] text-[#5C6B82]">100</span>
              </div>
            </div>

            {/* 30-Day History Bar Chart */}
            <div className="bg-[#131313] border border-white/5 p-6" data-testid="fear-greed-history">
              <h3 className="text-xs text-[#8892B0] uppercase tracking-widest font-bold mb-4">
                30-Day History
              </h3>
              <div className="flex items-end gap-[2px] h-40" data-testid="history-bars">
                {[...data].reverse().map((day, i) => {
                  const val = parseInt(day.value) || 0;
                  const height = (val / maxBarVal) * 100;
                  return (
                    <div
                      key={day.timestamp}
                      className="flex-1 relative group cursor-pointer"
                      style={{ height: '100%' }}
                    >
                      <div
                        className="absolute bottom-0 w-full rounded-t transition-all hover:opacity-80"
                        style={{
                          height: `${height}%`,
                          backgroundColor: getColor(val),
                          minHeight: '2px'
                        }}
                      />
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                        <div className="bg-[#0A0A0A] border border-white/10 px-2 py-1 rounded whitespace-nowrap">
                          <p className="text-xs font-bold" style={{ color: getColor(val) }}>{val} — {getLabel(val)}</p>
                          <p className="text-[10px] text-[#5C6B82]">{formatDate(day.timestamp)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[10px] text-[#5C6B82]">30 days ago</span>
                <span className="text-[10px] text-[#5C6B82]">Today</span>
              </div>
            </div>

            <p className="mt-4 text-xs text-[#5C6B82] text-center">
              Data from Alternative.me &middot; Not financial advice
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default FearGreedPage;
